import { FC, useEffect } from "react";
import {
    TrashIcon,
    HomeIcon,
    CalendarIcon,
    PencilSquareIcon,
    ClockIcon
} from "@heroicons/react/24/outline";

export interface MemoContent {
    uuid: string;
    day: number;
    title: string;
    description?: string;
    type: "property" | "experience";
    notes?: string;
    time?: string; // ISO 8601 format
}

export interface DroppedItem {
    success: boolean;
    data: MemoContent;
}


export interface HolidayPackageComposerProps {
    days?: number;
    onDrop?: (event: any) => DroppedItem;
    onDragEnter?: (event: any) => void;
    onDragOver?: (event: any) => void;
    onDragLeave?: (event: any) => void;
    onDeleteItem?: (item:MemoContent) => void;
    onSetTime?: (item: MemoContent) => void;
    onSetNotes?: (item: MemoContent) => void;
    title: string;
    description: string;
    events?: any[];
}

const HolidayPackageComposer: FC<HolidayPackageComposerProps> = ({ 
        days, 
        onDrop,
        onDragEnter,
        onDragOver,
        onDragLeave,
        onDeleteItem,
        onSetTime,
        onSetNotes,
        title,
        description,
        events
}) => {

    let daysArray = Array.from({ length: days || 7 }, (_, i) => i + 1);

    // console.log("HolidayPackageComposer: daysArray", daysArray);
    // console.log("HolidayPackageComposer: events", events);

    return (<>
        <div className="flex flex-col gap-4 mb-4">
            <h2 className="text-2xl font-bold">{title}</h2>
            <p className="text-gray-600">{description}</p>
        </div>
        <div className="grid grid-flow-row auto-rows-max gap-2 grid-cols-1">
            {
                daysArray.map((day:number) => {
                    // console.log("HolidayPackageComposer: day", day);
                    // console.log("HolidayPackageComposer: events for day", day, events[day-1]);
                    return (
                        <HolidayPackageDay
                            key={`day-${day}`}
                            dayNumber={day}
                            onDrop={onDrop}
                            onDragEnter={onDragEnter}
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onDeleteItem={onDeleteItem}
                            onSetTime={onSetTime}
                            onSetNotes={onSetNotes}
                            // Pass the events for the specific day (index day-1 since days start from 1 and arrays from 0)
                            events={events[day-1] || []}
                        />
                    );
                })
            }
        </div>
    </>);
}

interface HolidayPackageDayProps {
    dayNumber: number;
    events?: MemoContent[];
    onDrop?: (event: any) => DroppedItem;
    onDragEnter?: (event: any) => void;
    onDragOver?: (event: any) => void;
    onDragLeave?: (event: any) => void;
    onDeleteItem?: (item:MemoContent) => void;
    onSetTime?: (item: MemoContent) => void;
    onSetNotes?: (item: MemoContent) => void;
}

const HolidayPackageDay: FC<HolidayPackageDayProps> = ({
    dayNumber,
    onDrop,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDeleteItem,
    onSetTime,
    onSetNotes,
    events
}) => {

    const handleDragOver = (event: any) => {
        event.stopPropagation();
        event.preventDefault();
        onDragOver(event);
    }

    const handleDragEnter = (event: any) => {
        event.stopPropagation();
        event.preventDefault();
        onDragEnter(event);
    }

    const handleDragLeave = (event: any) => {
        event.stopPropagation();
        event.preventDefault();
        onDragLeave(event);
    }

    const handleDrop = (event: any) => {
        event.stopPropagation();
        event.preventDefault();
        const result: DroppedItem = onDrop(event);
        if (result.success) {
            console.debug("Drop successful");
        } else {
            console.debug("Drop failed");
        }
    };

    const handleDeleteItem = (index: number) => {
        onDeleteItem(events[index]);
    }

    const handleSetTime = (item: MemoContent) => {
        onSetTime(item);
    }

    const handleSetNotes = (item: MemoContent) => {
        onSetNotes(item);
    }

    // console.log("HolidayPackageDay: dayNumber", dayNumber, "events", events);

    // rerender the day with the events
    useEffect(() => {
    }, [events]);

    return (
        <div
            className="border-2 border-gray-100 p-4 rounded-lg transition duration-200 ease-in-out"
            key={`day-${dayNumber}`}
        >
            <h3 className="text-lg font-semibold">Day {dayNumber}</h3>
            <div 
                className="flex flex-col gap-2 min-h-32 bg-gray-50 p-4 rounded-lg border-dashed border-2 border-gray-300"
                onDragOver={(e) => handleDragOver(e)}
                onDragEnter={(e) => handleDragEnter(e)}
                onDragLeave={(e) => handleDragLeave(e)}
                onDrop={(e) => handleDrop(e)}
                data-day-number={dayNumber}
            >
                {events.map((content, index) => (
                    <div key={content.uuid || `event-${dayNumber}-${index}`} className="flex flex-col bg-gray-200 p-2 rounded-md">
                        <div className="flex flex-row items-center justify-between">
                            <div className="flex flex-row items-center justify-start gap-2">
                                { content.type === "property" &&
                                    <div className="text-black-500 hover:text-black-700">
                                        <HomeIcon className="w-5 h-5" />
                                    </div>
                                }
                                { content.type === "experience" &&
                                    <div className="text-black-500 hover:text-black-700">
                                        <CalendarIcon className="w-5 h-5" />
                                    </div>
                                }
                                <div className="text-sm font-medium">{content.title}</div>
                            </div>
                            <div className="flex flex-row items-center justify-start gap-2">
                                <div 
                                    className="text-gray-500 hover:text-gray-700"
                                    onClick={() => handleSetNotes(content)}
                                >
                                    <PencilSquareIcon className="w-5 h-5" />
                                </div>
                                { content.type === "experience" &&
                                    <div 
                                        className="text-gray-500 hover:text-gray-700"
                                        onClick={() => handleSetTime(content)}
                                    >
                                        <ClockIcon className="w-5 h-5" />
                                    </div>
                                }
                                <div
                                    className="text-red-500 hover:text-red-700"
                                    onClick={() => handleDeleteItem(index)}
                                >
                                    <TrashIcon className="w-5 h-5" />
                                </div>
                            </div>
                        </div>
                        {content.time && (
                            <div className="text-sm text-gray-700 mt-1">
                                <span className="font-medium">Time:</span> {content.time}
                            </div>
                        )}
                        {content.notes && (
                            <div className="text-sm text-gray-700 mt-1">
                                <span className="font-medium">Notes:</span> {content.notes}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default HolidayPackageComposer;
